import { useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  clearTppBorrowDraft,
  loadTppBorrowDraft,
  saveTppBorrowDraft,
} from './tppBorrowDraft';
import {
  TPP_MODAL_CONTENT_CLASS,
  TppBtnSpinner,
  TppDateField,
  TppRequiredMark,
  tppTodayIso,
} from './tppUiShared';
import { useTppOnModalFreshOpen } from './useTppOnModalFreshOpen';
import type { TppBorrowSubmitPayload } from './tppOpsModalTypes';

type Props = {
  show: boolean;
  onHide: () => void;
  closeLabel: string;
  money: (n: number) => string;
  balanceNumber: number | null;
  /** 他カードがすでに貸出中のとき注意を出す */
  otherCheckedOutCount?: number;
  pending: boolean;
  onSubmit: (payload: TppBorrowSubmitPayload) => void;
};

export default function TppBorrowModal({
  show,
  onHide,
  closeLabel,
  money,
  balanceNumber,
  otherCheckedOutCount = 0,
  pending,
  onSubmit,
}: Props) {
  const { t } = useTranslation();
  const [outDate, setOutDate] = useState(() => tppTodayIso());
  const [purpose, setPurpose] = useState('');
  const [place, setPlace] = useState('');
  const [route, setRoute] = useState('');
  const [fromDraft, setFromDraft] = useState(false);

  useTppOnModalFreshOpen(show, () => {
    setOutDate(tppTodayIso());
    const draft = loadTppBorrowDraft();
    if (draft) {
      setPurpose(draft.purpose);
      setPlace(draft.place);
      setRoute(draft.route);
      setFromDraft(!!(draft.purpose || draft.place || draft.route));
    } else {
      setPurpose('');
      setPlace('');
      setRoute('');
      setFromDraft(false);
    }
  });

  const canSubmit = purpose.trim() && place.trim() && route.trim();

  return (
    <Modal show={show} onHide={onHide} centered scrollable contentClassName={TPP_MODAL_CONTENT_CLASS}>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          if (!pending && canSubmit) {
            const payload = { outDate, purpose, place, route };
            saveTppBorrowDraft(payload);
            onSubmit(payload);
          }
        }}
      >
        <Modal.Header closeButton closeLabel={closeLabel}>
          <Modal.Title>{t('tpp.borrow_modal_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-3">
            {t('tpp.borrow_required_hint')}
          </p>
          {fromDraft && (
            <Alert
              variant="light"
              className="border small py-2 mb-3 d-flex flex-wrap align-items-center justify-content-between gap-2"
            >
              <span>{t('tpp.borrow_draft_hint')}</span>
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                className="text-nowrap"
                onClick={() => {
                  setPurpose('');
                  setPlace('');
                  setRoute('');
                  setFromDraft(false);
                  clearTppBorrowDraft();
                }}
              >
                {t('tpp.borrow_draft_clear')}
              </Button>
            </Alert>
          )}
          {otherCheckedOutCount > 0 && (
            <Alert variant="warning" className="small py-2 mb-3">
              {t('tpp.borrow_other_checked_out_hint', { count: otherCheckedOutCount })}
            </Alert>
          )}
          {balanceNumber != null && (
            <p className="small mb-3">
              {t('tpp.borrow_balance_label', { balance: money(balanceNumber) })}
            </p>
          )}
          <TppDateField
            id="tpp-borrow-out-date"
            label={t('tpp.col_out')}
            value={outDate}
            onChange={setOutDate}
          />
          <Form.Group className="mb-2">
            <Form.Label htmlFor="tpp-borrow-purpose">
              {t('tpp.col_purpose')}
              <TppRequiredMark />
            </Form.Label>
            <Form.Control
              id="tpp-borrow-purpose"
              value={purpose}
              onChange={(e) => {
                setPurpose(e.target.value);
                setFromDraft(false);
              }}
              required
              autoFocus
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label htmlFor="tpp-borrow-place">
              {t('tpp.destination')}
              <TppRequiredMark />
            </Form.Label>
            <Form.Control
              id="tpp-borrow-place"
              value={place}
              onChange={(e) => {
                setPlace(e.target.value);
                setFromDraft(false);
              }}
              required
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label htmlFor="tpp-borrow-route">
              {t('tpp.route')}
              <TppRequiredMark />
            </Form.Label>
            <Form.Control
              id="tpp-borrow-route"
              as="textarea"
              rows={2}
              value={route}
              onChange={(e) => {
                setRoute(e.target.value);
                setFromDraft(false);
              }}
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={onHide}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={pending || !canSubmit}>
            <TppBtnSpinner show={pending} />
            {t('tpp.borrow_confirm')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
